import { Group, Switch, ActionIcon } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';

export interface AutoRefreshControlsProps {
  /** Whether auto-refresh is currently enabled */
  enabled: boolean;
  /** Callback when auto-refresh state changes */
  onEnabledChange: (enabled: boolean) => void;
  /** Optional label to display interval (e.g., "(5s)", "(10s)") */
  intervalLabel?: string;
  /** Optional callback for manual refresh button */
  onManualRefresh?: () => void;
  /** Whether a refresh is currently in progress */
  isRefreshing?: boolean;
  /** Optional custom buttons or elements to render alongside controls */
  children?: React.ReactNode;
}

/**
 * Reusable component for auto-refresh controls
 * 
 * Renders a Switch for toggling auto-refresh, an optional manual refresh button,
 * and supports custom action buttons via children.
 * 
 * @example
 * ```tsx
 * const { enabled, setEnabled } = useAutoRefresh({ interval: 5000 });
 * 
 * <AutoRefreshControls
 *   enabled={enabled}
 *   onEnabledChange={setEnabled}
 *   intervalLabel="(5s)"
 *   onManualRefresh={refetch}
 *   isRefreshing={isLoading}
 * />
 * ```
 * 
 * @example With custom buttons
 * ```tsx
 * <AutoRefreshControls enabled={enabled} onEnabledChange={setEnabled}>
 *   <Button onClick={customAction}>Custom Action</Button>
 * </AutoRefreshControls>
 * ```
 */
export function AutoRefreshControls({
  enabled,
  onEnabledChange,
  intervalLabel,
  onManualRefresh,
  isRefreshing,
  children,
}: AutoRefreshControlsProps) {
  const label = intervalLabel ? `Auto-refresh ${intervalLabel}` : 'Auto-refresh';

  return (
    <Group gap="xs">
      <Switch
        label={label}
        checked={enabled}
        onChange={(event) => onEnabledChange(event.currentTarget.checked)}
      />
      {children}
      {onManualRefresh && (
        <ActionIcon
          size="lg"
          variant="default"
          onClick={onManualRefresh}
          loading={isRefreshing}
          aria-label="Refresh"
        >
          <IconRefresh size={18} />
        </ActionIcon>
      )}
    </Group>
  );
}

